/**
 * The entity-group name a portfolio holds for an inventor — figure C1's
 * "Entity group · groups this inventor with the same company's other
 * inventors", which the figure draws as an EDITABLE field.
 *
 * WHERE IT LIVES, AND WHY. No table carries an entity group: the aggregator's
 * entity row has no such column, and the group is derived from the inventor's
 * `entity_name` (see entityGroups.ts). But the group is the PORTFOLIO's own
 * label — the company it files this inventor under — not the inventor's fact,
 * so a portfolio must be able to correct it without touching the inventor's
 * record. That is exactly the shape of the intake label beside it, so it uses
 * the same server-side preference route and the same read-modify-write hook.
 *
 * An override is applied wherever groups are built, so S5, S6 and the entity
 * links can never disagree about which group an inventor is in.
 */
import { useAggregatorTagMap } from './intake'

/** entity_id -> the group name this portfolio has set, when it has set one. */
export type EntityGroupNames = Record<string, string>

export function entityGroupNameKey(aggregatorId: string): string {
  return `aggregator_entity_group:${aggregatorId}`
}

/**
 * The group label to use for one inventor: the portfolio's own override when
 * it has set one, otherwise the name the record carries. Never an empty
 * string — an override that is cleared falls straight back to the record.
 */
export function groupNameFor(
  overrides: EntityGroupNames,
  entityId: string | undefined,
  recorded: string | undefined,
): string | undefined {
  const set = entityId ? overrides[entityId] : undefined
  const trimmed = set?.trim()
  return trimmed || recorded
}

/** Read and write this portfolio's entity-group names. */
export function useEntityGroupNames(aggregatorId: string | null | undefined) {
  const { tags, setTag } = useAggregatorTagMap(
    aggregatorId,
    entityGroupNameKey,
    'aggregator_entity_group',
  )
  return { groupNames: tags as EntityGroupNames, setGroupName: setTag }
}
